import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Trash2, Plus, Pencil, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { authFetch, apiErr } from "../../lib/adminApi";
import { AdminBar } from "./AdminCore";

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminPages() {
  const [pages, setPages] = useState([]);
  const [path, setPath] = useState("");
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    fetch(`${API}/api/pages`).then((r) => r.json()).then(setPages).catch(() => {});
  }, []);

  useEffect(() => { document.title = "Admin · Pages | Cedar Views"; load(); }, [load]);

  const addPage = async (e) => {
    e.preventDefault();
    if (!path.trim()) return;
    setBusy(true);
    try {
      const res = await authFetch("/api/pages", {
        method: "POST",
        body: JSON.stringify({
          path: path.trim(),
          seoTitle: title.trim() || path.trim(),
          cta: title.trim() || "Windows & Doors",
          theme: { accent: "#31465A", accentText: "#ffffff", brand: "Cedar Views", brochureLabel: "Download Brochure" },
          modules: [
            { type: "hero", image: "", kicker: "Cedar Views", lines: [title.trim() || "New Page"], subtitle: "", consultProduct: title.trim() || "Windows & Doors" },
          ],
        }),
      });
      const data = await res.json();
      if (!res.ok) { toast.error(apiErr(data.detail)); return; }
      toast.success("Page created");
      setPath(""); setTitle("");
      load();
    } finally { setBusy(false); }
  };

  const removePage = async (p) => {
    if (!window.confirm(`Delete the page ${p}? This takes it offline immediately.`)) return;
    const res = await authFetch(`/api/pages/spec?path=${encodeURIComponent(p)}`, { method: "DELETE" });
    if (res.ok) { toast.success("Page deleted"); load(); }
    else toast.error("Could not delete page");
  };

  return (
    <main className="pt-40 pb-24 min-h-screen bg-[#050A15]" data-testid="admin-pages">
      <div className="max-w-[1100px] mx-auto px-6 lg:px-10">
        <AdminBar title="Product & Brand Pages." />

        <form onSubmit={addPage} className="border border-white/12 p-8 grid grid-cols-1 md:grid-cols-3 gap-5 items-end mb-12" data-testid="pages-add-form">
          <div>
            <label className="font-mono-x text-[10px] tracking-[0.2em] uppercase text-white/50 block mb-2">URL path *</label>
            <input value={path} onChange={(e) => setPath(e.target.value)} data-testid="pages-path-input" placeholder="/windows/new-range/"
              className="w-full bg-transparent border-b border-white/25 focus:border-white outline-none text-white py-2 text-sm" />
          </div>
          <div>
            <label className="font-mono-x text-[10px] tracking-[0.2em] uppercase text-white/50 block mb-2">Page name</label>
            <input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="pages-title-input" placeholder="e.g. New Range"
              className="w-full bg-transparent border-b border-white/25 focus:border-white outline-none text-white py-2 text-sm" />
          </div>
          <button type="submit" disabled={busy || !path.trim()} data-testid="pages-add-btn" className="btn-solid justify-center disabled:opacity-40">
            <Plus size={14} /> Create page
          </button>
        </form>

        <div className="border border-white/12 divide-y divide-white/10" data-testid="pages-list">
          {pages.map((p) => (
            <div key={p.path} className="flex items-center justify-between px-6 py-4 gap-4" data-testid={`page-row${p.path.replaceAll("/", "-")}`}>
              <div className="min-w-0">
                <p className="text-white font-medium text-sm truncate">{p.cta || p.seoTitle || p.path}</p>
                <p className="font-mono-x text-[10px] text-white/35 mt-1 truncate">{p.path}</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <a href={p.path} target="_blank" rel="noopener noreferrer" className="p-2 text-white/50 hover:text-white transition-colors" title="View live">
                  <ExternalLink size={15} />
                </a>
                <Link to={`/admin/pages/edit?path=${encodeURIComponent(p.path)}`} data-testid={`page-edit${p.path.replaceAll("/", "-")}`}
                  className="inline-flex items-center gap-2 px-4 py-2 border border-white/20 text-white/80 hover:bg-white hover:text-[#0B162C] text-xs font-semibold tracking-[0.12em] uppercase transition-colors">
                  <Pencil size={13} /> Edit
                </Link>
                <button onClick={() => removePage(p.path)} data-testid={`page-delete${p.path.replaceAll("/", "-")}`}
                  className="p-2 text-white/50 hover:text-red-400 transition-colors" title="Delete page">
                  <Trash2 size={15} />
                </button>
              </div>
            </div>
          ))}
          {pages.length === 0 && <p className="px-6 py-8 text-white/40 text-sm">No pages yet.</p>}
        </div>
        <p className="text-white/35 text-xs mt-6">{pages.length} editable pages</p>
      </div>
    </main>
  );
}
